/**
 * exportUtils.js
 * PDF and CSV export utilities for Fatigue Shield platform reports.
 * Uses jspdf + jspdf-autotable for PDF, plain JS for CSV.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const AIRLINE   = "Aviation Operator";
const PLATFORM  = "Fatigue Shield — Crew Operations Platform";
const NAVY      = [15, 23, 42];
const NAVY_MID  = [30, 41, 59];
const SLATE     = [100, 116, 139];
const WHITE     = [255, 255, 255];
const LIGHT     = [248, 250, 252];
const LINE      = [226, 232, 240];

/**
 * exportPDF({ title, subtitle, columns, rows, filename, orientation })
 *
 * columns: [{ header: "Name", key: "name", width?: number }]
 * rows:    array of objects matching column keys
 */
export function exportPDF({
  title,
  subtitle  = "",
  columns,
  rows,
  filename  = "report.pdf",
  orientation = "landscape",
}) {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageW  = orientation === "landscape" ? 297 : 210;
  const pageH  = orientation === "landscape" ? 210 : 297;
  const now    = new Date().toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" });

  // ── Header bar ─────────────────────────────────────────────────────────────
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 22, "F");

  doc.setTextColor(...WHITE);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(AIRLINE, 14, 10);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(PLATFORM, 14, 16);
  doc.setFontSize(8);
  doc.text(now, pageW - 14, 16, { align: "right" });

  // ── Title block ─────────────────────────────────────────────────────────────
  doc.setTextColor(...NAVY);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(title, 14, 34);

  let startY = 38;
  if (subtitle) {
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...SLATE);
    doc.text(subtitle, 14, 41);
    startY = 46;
  }

  // Thin rule under title
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(14, startY, pageW - 14, startY);
  startY += 4;

  // ── Table ───────────────────────────────────────────────────────────────────
  const colStyles = {};
  columns.forEach((col, i) => {
    if (col.width) colStyles[i] = { cellWidth: col.width };
  });

  autoTable(doc, {
    head: [columns.map(c => c.header)],
    body: rows.map(row => columns.map(c => {
      const v = row[c.key];
      return v === null || v === undefined ? "" : String(v);
    })),
    startY,
    theme: "grid",
    columnStyles: colStyles,
    headStyles: {
      fillColor: NAVY_MID,
      textColor: WHITE,
      fontSize: 8.5,
      fontStyle: "bold",
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
      textColor: NAVY,
    },
    alternateRowStyles: { fillColor: LIGHT },
    styles: { lineColor: LINE, lineWidth: 0.2 },
    didDrawPage: (data) => {
      const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(7.5);
      doc.setTextColor(...SLATE);
      doc.text(`${AIRLINE} · Confidential`, 14, pageH - 8);
      doc.text(`Page ${pageNum}`, pageW - 14, pageH - 8, { align: "right" });
      doc.text(`Generated: ${now}`, pageW / 2, pageH - 8, { align: "center" });
    },
  });

  doc.save(filename);
}

/**
 * exportCSV({ headers, rows, filename })
 *
 * headers: [{ label: "Name", key: "name" }]
 * rows:    array of objects
 */
export function exportCSV({ headers, rows, filename = "export.csv" }) {
  const headerRow = headers.map(h => `"${h.label}"`).join(",");
  const bodyRows  = rows.map(row =>
    headers.map(h => {
      const v = row[h.key] ?? "";
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(",")
  );
  const csv  = [headerRow, ...bodyRows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Convenience: today's date string for filenames */
export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * exportMarketingBrochure(data)
 * Generates a professional multi-page marketing PDF brochure for Fatigue Shield.
 */
export function exportMarketingBrochure(data) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const GOLD = [245, 158, 11];
  const BLUE_DARK = [15, 23, 42];
  const BLUE_MID  = [30, 41, 59];
  const BLUE_ACC  = [59, 130, 246];
  const SLATE2    = [100, 116, 139];
  const now = new Date().toLocaleString("en-IN", { dateStyle: "long" });
  const modules = data.modules || [];
  const integrations = data.integrations || [];
  const fleet = data.fleet_stats || {};
  const crew = data.crew_stats || {};
  const frms = data.frms_stats || {};
  const compliance = data.compliance_standards || [];

  // ─── PAGE 1: Cover ──────────────────────────────────────────────────────────
  // Full-bleed background
  doc.setFillColor(...BLUE_DARK);
  doc.rect(0, 0, W, H, "F");

  // Gold accent bar (top)
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 4, "F");

  // Platform name
  doc.setTextColor(...GOLD);
  doc.setFontSize(42);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE", 18, 80);
  doc.setTextColor(255, 255, 255);
  doc.text("SHIELD", 18, 100);

  // Version badge
  doc.setFillColor(...BLUE_ACC);
  doc.roundedRect(18, 108, 28, 7, 1, 1, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("VERSION 3.0", 32, 113, { align: "center" });

  // Tagline
  doc.setTextColor(...SLATE2);
  doc.setFontSize(13);
  doc.setFont("helvetica", "normal");
  doc.text("Sovereign AI-Enabled Aviation", 18, 126);
  doc.text("Fatigue Risk Management Platform", 18, 133);

  // Demo airline
  doc.setTextColor(...GOLD);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(`Demo: ${data.airline_demo || "B737 MAX 8 Fleet"}`, 18, 148);

  // KPI highlights box
  doc.setFillColor(...BLUE_MID);
  doc.roundedRect(18, 160, W - 36, 60, 2, 2, "F");
  const kpis = [
    ["19", "Modules"],
    [`${fleet.aircraft || 37}`, "Aircraft"],
    [`${crew.total_crew || 275}`, "Crew"],
    [`${crew.compliance_pct || 95.5}%`, "Compliance"],
    ["6", "Integrations"],
    ["Phase 3", "DGCA FRMS"],
  ];
  kpis.forEach(([val, label], i) => {
    const col = i % 3, row = Math.floor(i / 3);
    const x = 28 + col * 58, y = 175 + row * 28;
    doc.setTextColor(...GOLD);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(val, x, y);
    doc.setTextColor(200, 210, 220);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.text(label, x, y + 6);
  });

  // Divider
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.5);
  doc.line(18, 228, W - 18, 228);

  // Compliance badges
  doc.setTextColor(...SLATE2);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  const badges = ["DGCA CAR-7", "ICAO Annex 6", "ICAO Doc 9966", "EASA ORO.FTL"];
  badges.forEach((b, i) => {
    doc.setFillColor(30, 41, 59);
    doc.roundedRect(18 + i * 46, 232, 42, 7, 1, 1, "F");
    doc.setTextColor(200, 210, 220);
    doc.text(b, 39 + i * 46, 237, { align: "center" });
  });

  // Footer
  doc.setTextColor(...SLATE2);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${now}  ·  Confidential — For Authorised Personnel Only`, W / 2, H - 12, { align: "center" });

  // Gold accent bar (bottom)
  doc.setFillColor(...GOLD);
  doc.rect(0, H - 4, W, 4, "F");

  // ─── PAGE 2: Module Capabilities ───────────────────────────────────────────
  doc.addPage();
  doc.setFillColor(...BLUE_DARK);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE SHIELD · PLATFORM CAPABILITIES", 14, 11);
  doc.setTextColor(180, 190, 200);
  doc.text(now, W - 14, 11, { align: "right" });

  doc.setTextColor(...BLUE_DARK);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Module Capabilities", 14, 30);

  const catColors = {
    Operations:   [59, 130, 246],
    Scheduling:   [16, 185, 129],
    Compliance:   [245, 158, 11],
    Engineering:  [168, 85, 247],
    Intelligence: [236, 72, 153],
    AI:           [239, 68, 68],
    Planning:     [20, 184, 166],
    Analytics:    [99, 102, 241],
    Safety:       [234, 88, 12],
    Connectivity: [0, 188, 212],
    Governance:   [107, 114, 128],
  };

  autoTable(doc, {
    head: [["#", "Module", "Category", "Status"]],
    body: modules.map((m, i) => [i + 1, m.name, m.category, m.status]),
    startY: 36,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 100 },
      2: { cellWidth: 48 },
      3: { cellWidth: 24, halign: "center" },
    },
    headStyles: { fillColor: BLUE_MID, textColor: WHITE, fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 8.5, textColor: BLUE_DARK, cellPadding: 3 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 2) {
        const cat = data.cell.raw;
        const rgb = catColors[cat] || [100, 116, 139];
        data.cell.styles.textColor = rgb;
        data.cell.styles.fontStyle = "bold";
      }
      if (data.section === "body" && data.column.index === 3 && data.cell.raw === "Live") {
        data.cell.styles.textColor = [16, 185, 129];
        data.cell.styles.fontStyle = "bold";
      }
    },
    didDrawPage: ({ pageCount }) => {
      doc.setFontSize(7.5);
      doc.setTextColor(...SLATE2);
      doc.text("Fatigue Shield · Confidential", 14, H - 8);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });
    },
  });

  // ─── PAGE 3: Integration Ecosystem ─────────────────────────────────────────
  doc.addPage();
  doc.setFillColor(...BLUE_DARK);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE SHIELD · INTEGRATION ECOSYSTEM", 14, 11);
  doc.setTextColor(180, 190, 200);
  doc.text(now, W - 14, 11, { align: "right" });

  doc.setTextColor(...BLUE_DARK);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("External System Integrations", 14, 30);

  autoTable(doc, {
    head: [["Integration", "Vendor / System", "Category", "Data Direction", "Protocol"]],
    body: [
      ["AMOS / Ramco MRO",   "Swiss-AS / Ramco Systems",   "Maintenance",       "Bidirectional",  "REST + OAuth 2.0"],
      ["Jeppesen / AIMS",    "Boeing Jeppesen / AIMS",      "Crew Management",   "Bidirectional",  "SAML 2.0 + JWT"],
      ["ACARS FDP Feed",     "SITA / ARINC Network",        "Flight Operations", "Inbound stream", "X.509 / ACARS"],
      ["DGCA SDMS",          "DGCA Government Portal",      "Regulatory",        "Outbound",       "GOV PKI + OTP"],
      ["Claude 4 Sonnet",    "Anthropic (via Emergent)",    "AI Engine",         "Bidirectional",  "REST API"],
      ["AviationStack",      "AviationStack.com",           "Live Flight Data",  "Inbound",        "REST API"],
    ],
    startY: 36,
    theme: "grid",
    headStyles: { fillColor: BLUE_MID, textColor: WHITE, fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 8.5, textColor: BLUE_DARK, cellPadding: 3 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
    didDrawPage: () => {
      doc.setFontSize(7.5); doc.setTextColor(...SLATE2);
      doc.text("Fatigue Shield · Confidential", 14, H - 8);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });
    },
  });

  // ─── PAGE 4: Compliance & Fleet Stats ──────────────────────────────────────
  doc.addPage();
  doc.setFillColor(...BLUE_DARK);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE SHIELD · COMPLIANCE & FLEET OVERVIEW", 14, 11);
  doc.setTextColor(180, 190, 200);
  doc.text(now, W - 14, 11, { align: "right" });

  doc.setTextColor(...BLUE_DARK);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Regulatory Compliance Standards", 14, 30);

  autoTable(doc, {
    head: [["#", "Standard / Regulation"]],
    body: compliance.map((c, i) => [i + 1, c]),
    startY: 36,
    theme: "grid",
    columnStyles: { 0: { cellWidth: 14, halign: "center" }, 1: { cellWidth: 160 } },
    headStyles: { fillColor: BLUE_MID, textColor: WHITE, fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 9, textColor: BLUE_DARK, cellPadding: 3 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
  });

  const tableEndY = (doc.lastAutoTable?.finalY || 100) + 10;

  // Fleet & Crew stats
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...BLUE_DARK);
  doc.text("Fleet & Crew Statistics", 14, tableEndY);

  const statsData = [
    ["Aircraft (B737 MAX 8)", fleet.aircraft || 37, "Active", fleet.active || 35],
    ["Bases", fleet.bases || 6, "In Maintenance", fleet.in_maintenance || 3],
    ["Total Crew", crew.total_crew || 275, "Training Records", crew.training_records || 2820],
    ["Captains", crew.captains || 60, "First Officers", crew.first_officers || 65],
    ["Cabin Crew", crew.cabin_crew || 150, "Training Compliance", `${crew.compliance_pct || 95.5}%`],
  ];

  autoTable(doc, {
    head: [["Metric", "Value", "Metric", "Value"]],
    body: statsData,
    startY: tableEndY + 6,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 55 }, 1: { cellWidth: 35, halign: "center", fontStyle: "bold" },
      2: { cellWidth: 55 }, 3: { cellWidth: 35, halign: "center", fontStyle: "bold" },
    },
    headStyles: { fillColor: BLUE_MID, textColor: WHITE, fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 9, textColor: BLUE_DARK, cellPadding: 3 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
    didParseCell: (data) => {
      if (data.section === "body" && (data.column.index === 1 || data.column.index === 3)) {
        data.cell.styles.textColor = BLUE_ACC;
      }
    },
    didDrawPage: () => {
      doc.setFontSize(7.5); doc.setTextColor(...SLATE2);
      doc.text("Fatigue Shield · Confidential", 14, H - 8);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });
    },
  });

  // ─── PAGE 5: FRMS Highlights ────────────────────────────────────────────────
  doc.addPage();
  doc.setFillColor(...BLUE_DARK);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE SHIELD · FRMS PHASE 3 HIGHLIGHTS", 14, 11);
  doc.setTextColor(180, 190, 200);
  doc.text(now, W - 14, 11, { align: "right" });

  doc.setTextColor(...BLUE_DARK);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("FRMS — Phase 3 Capabilities", 14, 30);

  autoTable(doc, {
    head: [["Component", "Details", "Regulatory Reference"]],
    body: [
      ["FRMS Policy Library",       `${frms.policy_documents || 6} documents including Company Fatigue Policy, Procedures Manual, CFRP`,  "DGCA CAR Section 7"],
      ["Fatigue Hazard Register",   `${frms.hazards_registered || 7} hazards catalogued with risk ratings, controls and residual risk`, "ICAO Doc 9966 Ch.4"],
      ["Occurrence Reporting",       "Confidential non-punitive crew fatigue reporting with DGCA SDMS auto-submission",                    "DGCA CAR Section 5"],
      ["Scientific Evidence",        "SAFE v4.2 bio-mathematical model validation across 185 crew, 4284 flights",                         "ICAO Doc 9966 Ch.5"],
      ["FRMS Audit Trail",           "Internal + DGCA external audits with corrective action tracking",                                   "ICAO Doc 9966 Ch.6"],
      ["Compliance Dashboard",       `${frms.compliance_score || 93.5}% compliance score — Advanced FRMS status`,                         "DGCA CAR-OPS 1"],
    ],
    startY: 36,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 48, fontStyle: "bold" },
      1: { cellWidth: 98 },
      2: { cellWidth: 40 },
    },
    headStyles: { fillColor: BLUE_MID, textColor: WHITE, fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 8.5, textColor: BLUE_DARK, cellPadding: 3.5 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
  });

  const frmsY = (doc.lastAutoTable?.finalY || 140) + 12;

  // Contact/CTA box
  doc.setFillColor(...BLUE_MID);
  doc.roundedRect(14, frmsY, W - 28, 40, 2, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Ready to deploy Fatigue Shield?", 105, frmsY + 12, { align: "center" });
  doc.setTextColor(200, 210, 220);
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.text("Full-stack aviation fatigue management — from crew roster to FRMS Phase 3 regulatory compliance.", 105, frmsY + 22, { align: "center" });
  doc.text("AI-powered disruption management · 37-aircraft fleet simulation · DGCA Phase 1/2/3 alignment", 105, frmsY + 30, { align: "center" });

  doc.setFillColor(...GOLD);
  doc.rect(0, H - 4, W, 4, "F");
  doc.setTextColor(...SLATE2);
  doc.setFontSize(7.5);
  doc.text("Fatigue Shield · Confidential Platform Capability Document · " + now, 105, H - 8, { align: "center" });
  doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });

  doc.save(`fatigue-shield-brochure-${new Date().toISOString().slice(0,10)}.pdf`);
}

/**
 * exportPricingReport({ inputs, metrics, scenarios, currency })
 * Generates a branded 3-page pricing impact report for sales discussions.
 */
export function exportPricingReport({ inputs, metrics, scenarios = [], currency = "INR", exchangeRate = 84 }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const GOLD  = [245, 158, 11];
  const NAVY  = [15, 23, 42];
  const MID   = [30, 41, 59];
  const ACC   = [59, 130, 246];
  const SLATE = [100, 116, 139];
  const GREEN = [16, 185, 129];
  const now   = new Date().toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" });

  function fmtV(val, dp = 0) {
    if (currency === "INR") {
      if (val >= 10000000) return `Rs.${(val / 10000000).toFixed(2)}Cr`;
      if (val >= 100000)   return `Rs.${(val / 100000).toFixed(1)}L`;
      if (val >= 1000)     return `Rs.${(val / 1000).toFixed(1)}K`;
      return `Rs.${val.toFixed(dp)}`;
    }
    if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
    if (val >= 1000)    return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(dp)}`;
  }
  function fmtS(val) {
    return currency === "INR" ? `Rs.${val.toFixed(3)}` : `$${val.toFixed(4)}`;
  }

  // ── PAGE 1: Cover + Key Metrics ─────────────────────────────────────────
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, H, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 3, "F");
  doc.rect(0, H - 3, W, 3, "F");

  // Title
  doc.setTextColor(...GOLD);
  doc.setFontSize(36);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE", 18, 52);
  doc.setTextColor(255, 255, 255);
  doc.text("SHIELD", 18, 68);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SLATE);
  doc.text("Pricing Impact Analysis", 18, 78);

  // Airline name badge
  doc.setFillColor(...MID);
  doc.roundedRect(18, 88, W - 36, 10, 1, 1, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(`Prepared for: ${inputs.airline_name || "Airline"}`, 24, 95);
  doc.setTextColor(160, 180, 200);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.text(now, W - 24, 95, { align: "right" });

  // Key metrics box
  doc.setFillColor(...MID);
  doc.roundedRect(18, 106, W - 36, 72, 2, 2, "F");

  const kpis = metrics ? [
    ["Subscription / Aircraft",   fmtV(inputs.subscription_per_aircraft)],
    ["Cost per Revenue Seat",     fmtS(metrics.costPerRevenueSeat)],
    ["% of Annual Revenue",       `${metrics.pctOfRevenue?.toFixed(4)}%`],
    ["Monthly Fleet Cost",        fmtV(metrics.monthlyFleetCost)],
    ["Annual Fleet Cost",         fmtV(metrics.annualFleetCost)],
    ["Annual Fleet Revenue",      fmtV(metrics.annualFleetRevenue)],
  ] : [];

  kpis.forEach(([label, val], i) => {
    const col = i % 3, row = Math.floor(i / 3);
    const x = 28 + col * 56, y = 120 + row * 30;
    doc.setTextColor(...GOLD);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(val, x, y);
    doc.setTextColor(160, 180, 200);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(label, x, y + 7);
  });

  // Inputs summary
  const inputRows = metrics ? [
    ["Fleet Size",              `${inputs.fleet_size} aircraft`],
    ["Seats per A/C",           `${inputs.seats_per_aircraft}`],
    ["Sectors / Day / A/C",     `${inputs.sectors_per_day}`],
    ["Load Factor",             `${inputs.load_factor}%`],
    ["Average Fare",            fmtV(inputs.avg_fare)],
    ["Subscription / A/C",      fmtV(inputs.subscription_per_aircraft)],
  ] : [];

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Configuration Inputs", 18, 196);

  autoTable(doc, {
    head: [["Parameter", "Value", "Parameter", "Value"]],
    body: [
      [inputRows[0]?.[0] || "", inputRows[0]?.[1] || "", inputRows[3]?.[0] || "", inputRows[3]?.[1] || ""],
      [inputRows[1]?.[0] || "", inputRows[1]?.[1] || "", inputRows[4]?.[0] || "", inputRows[4]?.[1] || ""],
      [inputRows[2]?.[0] || "", inputRows[2]?.[1] || "", inputRows[5]?.[0] || "", inputRows[5]?.[1] || ""],
    ],
    startY: 200,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 45, fontStyle: "bold" }, 1: { cellWidth: 45 },
      2: { cellWidth: 45, fontStyle: "bold" }, 3: { cellWidth: 45 },
    },
    headStyles: { fillColor: MID, textColor: [255, 255, 255], fontSize: 7.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 8, textColor: [220, 230, 240], cellPadding: 2.5, fillColor: [20, 30, 50] },
    styles: { lineColor: [40, 55, 80], lineWidth: 0.2 },
  });

  // Sales insight box
  const tblEnd = (doc.lastAutoTable?.finalY || 230) + 8;
  if (metrics) {
    doc.setFillColor(20, 35, 60);
    doc.roundedRect(18, tblEnd, W - 36, 28, 1, 1, "F");
    doc.setFillColor(...GOLD);
    doc.rect(18, tblEnd, 2, 28, "F");
    doc.setTextColor(...GOLD);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text("KEY INSIGHT", 24, tblEnd + 7);
    doc.setTextColor(200, 215, 230);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const insight = `Fatigue Shield costs ${inputs.airline_name} just ${fmtV(inputs.subscription_per_aircraft)} per aircraft per month. Each booked ticket carries only ${fmtS(metrics.costPerRevenueSeat || 0)} of fatigue-management overhead — just ${(metrics.pctOfRevenue || 0).toFixed(4)}% of annual fleet revenue of ${fmtV(metrics.annualFleetRevenue || 0)}.`;
    const lines = doc.splitTextToSize(insight, W - 44);
    doc.text(lines, 24, tblEnd + 15);
  }

  // Footer
  doc.setTextColor(...SLATE);
  doc.setFontSize(7);
  doc.text("Fatigue Shield · Pricing Impact Analysis · Confidential", W / 2, H - 8, { align: "center" });

  // ── PAGE 2: Detailed Metrics ────────────────────────────────────────────
  if (metrics) {
    doc.addPage();
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 18, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, 0, W, 2, "F");
    doc.setTextColor(...GOLD);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text("FATIGUE SHIELD · DETAILED PRICING METRICS", 14, 11);
    doc.setTextColor(180, 190, 200);
    doc.text(now, W - 14, 11, { align: "right" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.text(`Pricing Impact — ${inputs.airline_name}`, 14, 30);

    autoTable(doc, {
      head: [["Metric", "Value", "Context"]],
      body: [
        ["Subscription per Aircraft",   fmtV(inputs.subscription_per_aircraft),           "Unit price per tail per month"],
        ["Monthly Fleet Cost",          fmtV((inputs.subscription_per_aircraft || 0) * (inputs.fleet_size || 0)), `${inputs.fleet_size} A/C × ${fmtV(inputs.subscription_per_aircraft)}`],
        ["Rev Seats per A/C per Day",   String(Math.round(metrics.revenueSeatsPerAcPerDay || 0)), `${inputs.sectors_per_day} sectors × ${inputs.seats_per_aircraft} seats × ${inputs.load_factor}% LF`],
        ["Revenue per A/C per Day",     fmtV(metrics.revenuePerAcPerDay || 0),            `Rev seats × avg fare ${fmtV(inputs.avg_fare)}`],
        ["Daily Fleet Revenue",         fmtV(metrics.dailyFleetRevenue || 0),             `${inputs.fleet_size} aircraft`],
        ["Annual Fleet Revenue",        fmtV(metrics.annualFleetRevenue || 0),            "Daily × 365"],
        ["Annual Fleet Cost",           fmtV(metrics.annualFleetCost || 0),               "Monthly × 12"],
        ["Cost per Revenue Seat",       fmtS(metrics.costPerRevenueSeat || 0),            "Annual cost/A/C ÷ annual rev seats/A/C"],
        ["Platform Cost % of Revenue",  `${(metrics.pctOfRevenue || 0).toFixed(4)}%`,    "Annual fleet cost ÷ annual fleet revenue"],
      ],
      startY: 36,
      theme: "grid",
      columnStyles: {
        0: { cellWidth: 68, fontStyle: "bold" },
        1: { cellWidth: 42, halign: "right", fontStyle: "bold" },
        2: { cellWidth: 70 },
      },
      headStyles: { fillColor: MID, textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8, textColor: NAVY, cellPadding: 3 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 1) {
          data.cell.styles.textColor = ACC;
        }
      },
      didDrawPage: () => {
        doc.setFontSize(7.5); doc.setTextColor(...SLATE);
        doc.text("Fatigue Shield · Pricing Impact Analysis", 14, H - 8);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });
      },
    });
  }

  // ── PAGE 3: Scenario Comparison (if any saved) ──────────────────────────
  if (scenarios.length >= 2) {
    doc.addPage();
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 18, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, 0, W, 2, "F");
    doc.setTextColor(...GOLD);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text("FATIGUE SHIELD · SCENARIO COMPARISON", 14, 11);
    doc.setTextColor(180, 190, 200);
    doc.text(now, W - 14, 11, { align: "right" });

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.text("Scenario Comparison", 14, 30);

    // Build comparison table
    const metricRows = ["Airline", "Fleet Size", "Subscription/A/C", "Total Monthly Cost", "Cost/Revenue Seat", "% Impact on Revenue", "Cost/Sector", "Annual Cost"];
    const getFnVal = (s, label) => {
      const m = (() => {
        const { fleet_size: F, seats_per_aircraft: S, sectors_per_day: SPD, load_factor: LF,
                avg_fare: FARE, subscription_per_aircraft: SPA } = s;
        if (!F || !S || !SPD || !FARE || !SPA) return null;
        const lf = LF / 100;
        const totalMonthlyCost = SPA * F;
        const revenueSeatsPerAcPerDay = SPD * S * lf;
        const dailyFleetRevenue = revenueSeatsPerAcPerDay * FARE * F;
        const annualFleetRevenue = dailyFleetRevenue * 365;
        const annualFleetCost = SPA * F * 12;
        const costPerRevenueSeat = (SPA * 12) / (SPD * S * lf * 365);
        const pctOfRevenue = (annualFleetCost / annualFleetRevenue) * 100;
        const costPerSector = totalMonthlyCost / (F * SPD * 30);
        return { totalMonthlyCost, revenueSeatsPerAcPerDay, dailyFleetRevenue, annualFleetRevenue, annualFleetCost, costPerRevenueSeat, pctOfRevenue, costPerSector };
      })();
      const cur = s.currency || "INR";
      const fv = (v, dp = 0) => {
        if (cur === "INR") {
          if (v >= 10000000) return `Rs.${(v / 10000000).toFixed(2)}Cr`;
          if (v >= 100000)   return `Rs.${(v / 100000).toFixed(1)}L`;
          if (v >= 1000)     return `Rs.${(v / 1000).toFixed(1)}K`;
          return `Rs.${v.toFixed(dp)}`;
        }
        if (v >= 1000000) return `$${(v / 1000000).toFixed(2)}M`;
        if (v >= 1000)    return `$${(v / 1000).toFixed(1)}K`;
        return `$${v.toFixed(dp)}`;
      };
      const fs = (v) => cur === "INR" ? `Rs.${v.toFixed(3)}` : `$${v.toFixed(4)}`;
      switch (label) {
        case "Airline": return s.airline_name || "—";
        case "Fleet Size": return `${s.fleet_size} A/C`;
        case "Subscription/A/C": return fv(s.subscription_per_aircraft || 0);
        case "Total Monthly Cost": return fv((s.subscription_per_aircraft || 0) * (s.fleet_size || 0));
        case "Cost/Revenue Seat": return m ? fs(m.costPerRevenueSeat) : "—";
        case "% Impact on Revenue": return m ? `${m.pctOfRevenue.toFixed(4)}%` : "—";
        case "Cost/Sector": return m ? fs(m.costPerSector) : "—";
        case "Annual Cost": return m ? fv(m.annualFleetCost) : "—";
        default: return "—";
      }
    };

    const colW = Math.min(40, (W - 14 - 14 - 48) / Math.min(scenarios.length, 4));
    const colStyles = { 0: { cellWidth: 48, fontStyle: "bold" } };
    scenarios.slice(0, 4).forEach((_, i) => { colStyles[i + 1] = { cellWidth: colW, halign: "right" }; });

    autoTable(doc, {
      head: [["Metric", ...scenarios.slice(0, 4).map(s => s.name)]],
      body: metricRows.map(label => [label, ...scenarios.slice(0, 4).map(s => getFnVal(s, label))]),
      startY: 36,
      theme: "grid",
      columnStyles: colStyles,
      headStyles: { fillColor: MID, textColor: [255, 255, 255], fontSize: 8, fontStyle: "bold" },
      bodyStyles: { fontSize: 8, textColor: NAVY, cellPadding: 3 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { lineColor: [226, 232, 240], lineWidth: 0.2 },
      didDrawPage: () => {
        doc.setFontSize(7.5); doc.setTextColor(...SLATE);
        doc.text("Fatigue Shield · Pricing Impact Analysis", 14, H - 8);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });
      },
    });
  }

  // ── PAGES 4 & 5: Commercial Subscription Terms ───────────────────────────
  const xRate = exchangeRate || 84;
  const FN    = inputs.fleet_size || 0;

  // Formats an INR-base value into the user's selected currency display
  const fmtCur = (inrVal) => {
    if (currency === "USD") {
      const v = inrVal / xRate;
      if (v >= 1000000) return `$${(v / 1000000).toFixed(2)}M`;
      if (v >= 1000)    return `$${(v / 1000).toFixed(1)}K`;
      return `$${v.toFixed(0)}`;
    }
    if (inrVal >= 10000000) return `Rs.${(inrVal / 10000000).toFixed(2)}Cr`;
    if (inrVal >= 100000)   return `Rs.${(inrVal / 100000).toFixed(1)}L`;
    if (inrVal >= 1000)     return `Rs.${(inrVal / 1000).toFixed(0)}K`;
    return `Rs.${inrVal.toFixed(0)}`;
  };

  const CTIERS = [
    { term: "1 Year",  price: 200000, launchMult: 3   },
    { term: "3 Year",  price: 150000, launchMult: 2   },
    { term: "5 Year",  price: 100000, launchMult: 1.5 },
  ];

  // ── PAGE: Subscription Pricing + Launch Fee ──────────────────────────────
  doc.addPage();
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE SHIELD  \u00B7  COMMERCIAL SUBSCRIPTION TERMS", 14, 11);
  doc.setTextColor(180, 190, 200);
  doc.text(now, W - 14, 11, { align: "right" });

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Commercial Subscription Terms", 14, 29);

  // Overview callout box
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(14, 33, W - 28, 18, 1.5, 1.5, "F");
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  doc.roundedRect(14, 33, W - 28, 18, 1.5, 1.5, "S");
  doc.setFillColor(...GOLD);
  doc.rect(14, 33, 2, 18, "F");
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(40, 55, 80);
  const overviewTxt = "Fatigue Shield is offered as a fleet-aligned subscription platform, priced per aircraft per month, enabling airlines of every size to access enterprise-grade fatigue, safety, compliance, and operational intelligence. The platform continuously adapts to the airline's fleet growth, route network, staffing model, disruption profile, and regulatory environment.";
  const overviewLines = doc.splitTextToSize(overviewTxt, W - 36);
  doc.text(overviewLines, 20, 39.5);

  // ── 1. Subscription Pricing ──
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("1.  Subscription Pricing", 14, 57);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SLATE);
  doc.text("Standard Commercial Terms  \u00B7  Per Aircraft Per Month", 14, 62);

  const pricingRows = CTIERS.map((t) => {
    const fleetMonthly = t.price * FN;
    const fleetAnnual  = fleetMonthly * 12;
    const saving       = (CTIERS[0].price - t.price) * FN * 12;
    const listPrice    = `Rs.${parseFloat((t.price / 100000).toFixed(2))}L`;
    return [
      t.term,
      listPrice,
      fmtCur(fleetMonthly),
      fmtCur(fleetAnnual),
      t.price === CTIERS[0].price ? "\u2014" : `Save ${fmtCur(saving)} / yr`,
    ];
  });

  autoTable(doc, {
    head: [["Term", "List Price / A/C / Month", `Fleet Monthly (${FN} A/C)`, "Fleet Annual", "Annual Saving vs 1-Yr"]],
    body: pricingRows,
    startY: 65,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 22, fontStyle: "bold" },
      1: { cellWidth: 40, halign: "right" },
      2: { cellWidth: 40, halign: "right" },
      3: { cellWidth: 40, halign: "right" },
      4: { cellWidth: 36, halign: "right" },
    },
    headStyles: { fillColor: MID, textColor: WHITE, fontSize: 7.5, fontStyle: "bold", cellPadding: { top: 3, bottom: 3, left: 3, right: 3 } },
    bodyStyles: { fontSize: 8, textColor: NAVY, cellPadding: 3 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: LINE, lineWidth: 0.2 },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === 2) {
        // Highlight 5-year row (best value) in green
        data.cell.styles.fillColor = [235, 255, 244];
        if (data.column.index !== 0) {
          data.cell.styles.textColor = GREEN;
          data.cell.styles.fontStyle = "bold";
        }
      }
      if (data.section === "body" && data.row.index === 1) {
        // Highlight 3-year row in soft blue
        data.cell.styles.fillColor = [240, 246, 255];
      }
    },
  });

  let p4Y = (doc.lastAutoTable?.finalY || 115) + 4;

  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...SLATE);
  doc.text("* Longer-term partnerships receive preferential pricing based on strategic commitment, deeper integration, and continuous optimisation.", 14, p4Y);
  p4Y += 9;

  // ── 2. Operational Launch Fee ──
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("2.  Operational Launch Fee  (One-Time)", 14, p4Y);
  p4Y += 5;

  const launchRows = CTIERS.map((t) => [
    t.term,
    `${t.launchMult} months subscription value`,
    fmtCur(t.launchMult * t.price * FN),
  ]);

  autoTable(doc, {
    head: [["Term", "Fee Basis", `Estimated Launch Fee (${FN} A/C)`]],
    body: launchRows,
    startY: p4Y,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 22, fontStyle: "bold" },
      1: { cellWidth: 98 },
      2: { cellWidth: 58, halign: "right", fontStyle: "bold" },
    },
    headStyles: { fillColor: MID, textColor: WHITE, fontSize: 7.5, fontStyle: "bold", cellPadding: { top: 3, bottom: 3, left: 3, right: 3 } },
    bodyStyles: { fontSize: 8, textColor: NAVY, cellPadding: 3 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    styles: { lineColor: LINE, lineWidth: 0.2 },
  });

  p4Y = (doc.lastAutoTable?.finalY || p4Y + 42) + 7;

  // Launch fee coverage bullets
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("The Operational Launch Fee covers:", 14, p4Y);
  p4Y += 6;

  const feeItems = [
    "Airline operating model configuration",
    "Fatigue rules & regulatory alignment",
    "Workflow tailoring",
    "Data migration / onboarding",
    "User access & permissions",
    "Integration setup",
    "Training & go-live support",
  ];
  const feeHalf = Math.ceil(feeItems.length / 2);
  feeItems.forEach((item, idx) => {
    const col = idx < feeHalf ? 0 : 1;
    const row = col === 0 ? idx : idx - feeHalf;
    const bx  = 14 + col * 93;
    const by  = p4Y + row * 6.5;
    doc.setFillColor(...GOLD);
    doc.circle(bx + 1.5, by - 1, 1.2, "F");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...NAVY);
    doc.text(item, bx + 5.5, by);
  });

  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  doc.text("Fatigue Shield  \u00B7  Commercial Terms  \u00B7  Confidential", 14, H - 8);
  doc.text(now, W / 2, H - 8, { align: "center" });
  doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });

  // ── PAGE: What Is Included — Full Platform Access ─────────────────────────
  doc.addPage();
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 18, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 0, W, 2, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text("FATIGUE SHIELD  \u00B7  COMPLETE PLATFORM INCLUSIONS", 14, 11);
  doc.setTextColor(180, 190, 200);
  doc.text(now, W - 14, 11, { align: "right" });

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("What Is Included \u2014 Full Platform Access", 14, 29);

  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...SLATE);
  doc.text("All subscriptions include unrestricted access to the complete Fatigue Shield platform. No module restrictions. No hidden add-ons.", 14, 36);

  const inclCats = [
    {
      title: "Operations & Crew Intelligence", color: [59, 130, 246],
      items: ["Crew Fatigue Dashboard", "Crew Master Database", "Roster Planner", "Pairing Builder (AI Optimised)", "Swap Requests & Crew Changes", "Staffing Engine", "Fairness & Quality-of-Life Engine"],
    },
    {
      title: "Live Operations Control", color: [16, 185, 129],
      items: ["Disruption Engine", "Recovery Console", "Operations Console", "Live Ops Map", "Scenario Simulator", "Delay / FDP / Duty Risk Alerts"],
    },
    {
      title: "Safety, Compliance & FRMS", color: [245, 158, 11],
      items: ["Compliance Board", "DGCA CAR Section 7 Alignment", "ICAO Annex 6 Compliance Support", "ICAO Doc 9966 FRMS Framework", "EASA ORO.FTL Logic Support", "FRMS Phase 3 Module", "Fatigue Hazard Register", "Confidential Occurrence Reporting", "Audit Trails & Corrective Action"],
    },
    {
      title: "Engineering & CAMO", color: [168, 85, 247],
      items: ["CAMO Planning Module", "Maintenance Awareness Integration", "Airworthiness Planning Visibility", "Resource & Capacity Oversight"],
    },
    {
      title: "Analytics & AI", color: [236, 72, 153],
      items: ["Predictive Fatigue Modelling", "AI Pairing Optimisation", "Scenario Simulation AI", "Safety Analytics Dashboard", "Fatigue Trend Analysis", "Executive Risk Dashboards"],
    },
    {
      title: "Connectivity & Integration", color: [0, 188, 212],
      items: ["Integrations Hub", "Jeppesen / AIMS Connectivity", "AMOS / Ramco MRO Integration", "ACARS FDP Data Feed", "DGCA SDMS Submission Support", "Live Flight Data Integration", "Secure API Access"],
    },
    {
      title: "Support & Continuous Improvement", color: [99, 102, 241],
      items: ["Ongoing platform upgrades", "Regulatory rule updates", "Performance tuning", "Quarterly optimisation reviews", "Customer success support"],
    },
  ];

  const CBOX_COL_W = (W - 28 - 5) / 2;
  const CLEFT      = 14;
  const CRIGHT     = 14 + CBOX_COL_W + 5;
  const C_HDR_H    = 8;
  const C_PAD      = 3;
  const C_ITEM_H   = 5.4;

  // Helper to draw a single category box, returns box height
  const drawCatBox = (cat, bx, by, bw) => {
    const bh = C_HDR_H + C_PAD + cat.items.length * C_ITEM_H + C_PAD;
    // Light background
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(bx, by, bw, bh, 1.5, 1.5, "F");
    // Colored title bar (rounded top, square bottom)
    doc.setFillColor(...cat.color);
    doc.roundedRect(bx, by, bw, C_HDR_H, 1.5, 1.5, "F");
    doc.rect(bx, by + 3, bw, C_HDR_H - 3, "F");
    // Title text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text(cat.title, bx + 4, by + 5.5);
    // Item list
    cat.items.forEach((item, i) => {
      const iy = by + C_HDR_H + C_PAD + i * C_ITEM_H;
      doc.setFillColor(...cat.color);
      doc.circle(bx + 4.5, iy - 0.5, 1, "F");
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...NAVY);
      doc.text(item, bx + 8, iy + 0.5);
    });
    return bh;
  };

  let catY = 41;
  const catPairs = [
    [inclCats[0], inclCats[1]],
    [inclCats[2], inclCats[3]],
    [inclCats[4], inclCats[5]],
  ];
  catPairs.forEach(([lc, rc]) => {
    const lh = drawCatBox(lc, CLEFT,  catY, CBOX_COL_W);
    const rh = drawCatBox(rc, CRIGHT, catY, CBOX_COL_W);
    catY += Math.max(lh, rh) + 4;
  });

  // Support & Continuous Improvement — full-width, 2-column items layout
  const lastCat  = inclCats[6];
  const lastHalf = Math.ceil(lastCat.items.length / 2);
  const fullW    = W - 28;
  const lastBH   = C_HDR_H + C_PAD + Math.ceil(lastCat.items.length / 2) * C_ITEM_H + C_PAD;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(14, catY, fullW, lastBH, 1.5, 1.5, "F");
  doc.setFillColor(...lastCat.color);
  doc.roundedRect(14, catY, fullW, C_HDR_H, 1.5, 1.5, "F");
  doc.rect(14, catY + 3, fullW, C_HDR_H - 3, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text(lastCat.title, 18, catY + 5.5);
  lastCat.items.forEach((item, i) => {
    const col = i < lastHalf ? 0 : 1;
    const row = col === 0 ? i : i - lastHalf;
    const ix  = 14 + col * (fullW / 2) + 3;
    const iy  = catY + C_HDR_H + C_PAD + row * C_ITEM_H;
    doc.setFillColor(...lastCat.color);
    doc.circle(ix + 4, iy - 0.5, 1, "F");
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...NAVY);
    doc.text(item, ix + 7.5, iy + 0.5);
  });
  catY += lastBH + 5;

  // Closing CTA bar
  doc.setFillColor(...MID);
  doc.roundedRect(14, catY, W - 28, 18, 1.5, 1.5, "F");
  doc.setFillColor(...GOLD);
  doc.rect(14, catY, 2, 18, "F");
  doc.setTextColor(...GOLD);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("One subscription. Full airline platform.", 20, catY + 7);
  doc.setTextColor(180, 200, 220);
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.text("No module restrictions. No hidden add-ons. Priced for airlines of every size.", 20, catY + 13.5);
  doc.setTextColor(...GOLD);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(inputs.airline_name || "Your Airline", W - 16, catY + 7, { align: "right" });

  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  doc.text("Fatigue Shield  \u00B7  Commercial Terms  \u00B7  Confidential", 14, H - 8);
  doc.text(now, W / 2, H - 8, { align: "center" });
  doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, W - 14, H - 8, { align: "right" });

  doc.save(`fatigue-shield-pricing-${inputs.airline_name?.toLowerCase().replace(/\s+/g, "-") || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
